import { getAuthToken } from './auth';

const API_BASE_URL = 'https://kosarev.app/api/cards/v1';
const API_REVIEWS_URL = 'https://kosarev.app/api/reviews/v1';

// GROUPS API
export async function fetchCardGroups() {
    try {
        const token = getAuthToken();

        if (!token) {
            return {
                success: false,
                message: 'Authentication required',
                error: new Error('No auth token')
            };
        }

        const response = await fetch(`${API_BASE_URL}/group`, {
            method: 'GET',
            headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json'
            }
        });
        const data = await response.json();

        if (!response.ok) {
            return {
                success: false,
                message: data.message || `Failed to fetch groups: ${response.statusText}`,
                error: new Error(data.message || response.statusText),
                status: response.status
            };
        }

        return {
            success: true,
            data
        };
    } catch (error) {
        console.error('Error fetching card groups:', error);

        return {
            success: false,
            message: error.message || 'An unexpected error occurred when fetching groups',
            error
        };
    }
}

export async function fetchCardGroup(groupId) {
    try {
        const token = getAuthToken();

        if (!token) {
            return {
                success: false,
                message: 'Authentication required',
                error: new Error('No auth token')
            };
        }

        const response = await fetch(`${API_BASE_URL}/group/${groupId}`, {
            method: 'GET',
            headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json'
            }
        });

        const data = await response.json();

        if (!response.ok) {
            return {
                success: false,
                message: data.message || `Failed to fetch group: ${response.statusText}`,
                error: new Error(data.message || response.statusText),
                status: response.status
            };
        }

        console.log('API response for group:', data);

        return {
            success: true,
            data
        };
    } catch (error) {
        console.error('Error fetching card group:', error);

        return {
            success: false,
            message: error.message || 'An unexpected error occurred when fetching group',
            error
        };
    }
}

export async function createCardGroup(groupData) {
    try {
        const token = getAuthToken();

        if (!token) {
            return {
                success: false,
                message: 'Authentication required',
                error: new Error('No auth token')
            };
        }

        const requestData = {
            group_name: groupData.group_name,
            description: groupData.description || '',
            visibility: groupData.visibility || 'PRIVATE',
            card_side_types: groupData.card_side_types || []
        };

        const response = await fetch(`${API_BASE_URL}/group`, {
            method: 'POST',
            headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json'
            },
            body: JSON.stringify(requestData)
        });

        const data = await response.json();

        if (!response.ok) {
            return {
                success: false,
                message: data.message || `Failed to create group: ${response.statusText}`,
                error: new Error(data.message || response.statusText),
                status: response.status
            };
        }

        return {
            success: true,
            data
        };
    } catch (error) {
        console.error('Error creating card group:', error);

        return {
            success: false,
            message: error.message || 'An unexpected error occurred when creating group',
            error
        };
    }
}

export async function updateCardGroup(groupId, updateData) {
    try {
        const token = getAuthToken();

        if (!token) {
            return {
                success: false,
                message: 'Authentication required',
                error: new Error('No auth token')
            };
        }

        const requestData = {
            group_id: parseInt(groupId),
            group_name: updateData.group_name,
            description: updateData.description || '',
            visibility: updateData.visibility || 'PRIVATE',
            card_side_types: updateData.card_side_types || []
        };

        const response = await fetch(`${API_BASE_URL}/group/${groupId}`, {
            method: 'PUT',
            headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json'
            },
            body: JSON.stringify(requestData)
        });

        if (!response.ok) {
            let errorMessage;
            try {
                const errorData = await response.json();
                errorMessage = errorData.message;
            } catch (e) {
                errorMessage = response.statusText;
            }

            return {
                success: false,
                message: `Failed to update group: ${errorMessage}`,
                error: new Error(errorMessage),
                status: response.status
            };
        }

        return {
            success: true
        };
    } catch (error) {
        console.error('Error updating card group:', error);

        return {
            success: false,
            message: error.message || 'An unexpected error occurred when updating group',
            error
        };
    }
}

export async function deleteCardGroup(groupId) {
    try {
        const token = getAuthToken();

        if (!token) {
            return {
                success: false,
                message: 'Authentication required',
                error: new Error('No auth token')
            };
        }

        const response = await fetch(`${API_BASE_URL}/group/${groupId}`, {
            method: 'DELETE',
            headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json'
            }
        });

        if (!response.ok) {
            let errorMessage;
            try {
                const errorData = await response.json();
                errorMessage = errorData.message;
            } catch (e) {
                errorMessage = response.statusText;
            }

            return {
                success: false,
                message: `Failed to delete group: ${errorMessage}`,
                error: new Error(errorMessage),
                status: response.status
            };
        }

        return {
            success: true
        };
    } catch (error) {
        console.error('Error deleting card group:', error);

        return {
            success: false,
            message: error.message || 'An unexpected error occurred when deleting group',
            error
        };
    }
}

// CARDS API
export async function fetchGroupCards(groupId) {
    try {
        const token = getAuthToken();

        if (!token) {
            return {
                success: false,
                message: 'Authentication required',
                error: new Error('No auth token')
            };
        }

        const response = await fetch(`${API_BASE_URL}/group/${groupId}/cards`, {
            method: 'GET',
            headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json'
            }
        });

        const data = await response.json();

        if (!response.ok) {
            return {
                success: false,
                message: data.message || `Failed to fetch cards: ${response.statusText}`,
                error: new Error(data.message || response.statusText),
                status: response.status
            };
        }

        console.log('Raw API response for cards:', data);

        if (data.cards && Array.isArray(data.cards)) {
            data.cards.forEach((card, index) => {
                console.log(`Card ${index + 1}:`, {
                    id: card.id,
                    sides: card.sides,
                    front_text: card.front_text,
                    back_text: card.back_text
                });

                if (card.sides && Array.isArray(card.sides)) {
                    card.sides.forEach((side, sideIndex) => {
                        console.log(`  Side ${sideIndex + 1}:`, {
                            text: side.text,
                            reading_pairs: side.reading_pairs || side.readingPairs,
                            reading_pairs_count: (side.reading_pairs || side.readingPairs || []).length
                        });
                    });
                }
            });
        }

        const normalizedData = {
            ...data,
            cards: (data.cards || []).map(card => {
                return {
                    ...card,
                    frontText: card.sides?.[0]?.text || card.front_text || '',
                    backText: card.sides?.[1]?.text || card.back_text || ''
                };
            })
        };

        return {
            success: true,
            data: normalizedData
        };
    } catch (error) {
        console.error('Error fetching group cards:', error);

        return {
            success: false,
            message: error.message || 'An unexpected error occurred when fetching cards',
            error
        };
    }
}

export async function addCard(cardData) {
    try {
        const token = getAuthToken();

        if (!token) {
            return {
                success: false,
                message: 'Authentication required',
                error: new Error('No auth token')
            };
        }

        const requestData = {
            group_id: parseInt(cardData.group_id),
            sides_text: cardData.sides_text,
            front_text: cardData.sides_text[0] || '',
            back_text: cardData.sides_text[1] || ''
        };

        console.log('Adding card with data:', requestData);

        const response = await fetch(`${API_BASE_URL}/card`, {
            method: 'POST',
            headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json'
            },
            body: JSON.stringify(requestData)
        });

        const data = await response.json();

        if (!response.ok) {
            return {
                success: false,
                message: data.message || `Failed to add card: ${response.statusText}`,
                error: new Error(data.message || response.statusText),
                status: response.status
            };
        }

        return {
            success: true,
            data
        };
    } catch (error) {
        console.error('Error adding card:', error);

        return {
            success: false,
            message: error.message || 'An unexpected error occurred when adding card',
            error
        };
    }
}

export async function updateCard(cardId, cardData) {
    try {
        const token = getAuthToken();

        if (!token) {
            return {
                success: false,
                message: 'Authentication required',
                error: new Error('No auth token')
            };
        }

        const requestData = {
            card_id: parseInt(cardId),
            sides_text: cardData.sides_text,
            front_text: cardData.sides_text[0] || '',
            back_text: cardData.sides_text[1] || ''
        };

        console.log('Updating card with data:', requestData);

        const response = await fetch(`${API_BASE_URL}/card/${cardId}`, {
            method: 'PUT',
            headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json'
            },
            body: JSON.stringify(requestData)
        });

        if (!response.ok) {
            let errorMessage;
            try {
                const errorData = await response.json();
                errorMessage = errorData.message;
            } catch (e) {
                errorMessage = response.statusText;
            }

            return {
                success: false,
                message: `Failed to update card: ${errorMessage}`,
                error: new Error(errorMessage),
                status: response.status
            };
        }

        return {
            success: true
        };
    } catch (error) {
        console.error('Error updating card:', error);

        return {
            success: false,
            message: error.message || 'An unexpected error occurred when updating card',
            error
        };
    }
}

export async function deleteCard(cardId) {
    try {
        const token = getAuthToken();

        if (!token) {
            return {
                success: false,
                message: 'Authentication required',
                error: new Error('No auth token')
            };
        }

        const response = await fetch(`${API_BASE_URL}/card/${cardId}`, {
            method: 'DELETE',
            headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json'
            }
        });

        if (!response.ok) {
            let errorMessage;
            try {
                const errorData = await response.json();
                errorMessage = errorData.message;
            } catch (e) {
                errorMessage = response.statusText;
            }

            return {
                success: false,
                message: `Failed to delete card: ${errorMessage}`,
                error: new Error(errorMessage),
                status: response.status
            };
        }

        return {
            success: true
        };
    } catch (error) {
        console.error('Error deleting card:', error);

        return {
            success: false,
            message: error.message || 'An unexpected error occurred when deleting card',
            error
        };
    }
}

// REVIEWS API
export async function fetchCardsProgress(groupId) {
    try {
        const token = getAuthToken();

        if (!token) {
            return {
                success: false,
                message: 'Authentication required',
                error: new Error('No auth token')
            };
        }

        const response = await fetch(`${API_REVIEWS_URL}/review/${groupId}/progress`, {
            method: 'GET',
            headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json'
            }
        });

        const data = await response.json();

        if (!response.ok) {
            return {
                success: false,
                message: data.message || `Failed to fetch cards progress: ${response.statusText}`,
                error: new Error(data.message || response.statusText),
                status: response.status
            };
        }

        return {
            success: true,
            data
        };
    } catch (error) {
        console.error('Error fetching cards progress:', error);

        return {
            success: false,
            message: error.message || 'An unexpected error occurred when fetching cards progress',
            error
        };
    }
}

export async function getReviewCards(groupId, cardsCount) {
    try {
        const token = getAuthToken();

        if (!token) {
            return {
                success: false,
                message: 'Authentication required',
                error: new Error('No auth token')
            };
        }

        console.log('Requesting review cards for group:', groupId, 'count:', cardsCount);

        const response = await fetch(`${API_REVIEWS_URL}/review/${groupId}/cards`, {
            method: 'POST',
            headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json'
            },
            body: JSON.stringify({
                group_id: parseInt(groupId),
                cards_count: parseInt(cardsCount)
            })
        });

        const data = await response.json();
        console.log('Raw review cards response:', data);

        if (!response.ok) {
            return {
                success: false,
                message: data.message || `Failed to get review cards: ${response.statusText}`,
                error: new Error(data.message || response.statusText),
                status: response.status
            };
        }

        const cards = data.fullCards;
        console.log('Extracted cards:', cards);
        console.log('Available data keys:', Object.keys(data));

        const result = {
            success: true,
            data: {
                cards: cards
            }
        };

        console.log('Final result:', result);
        return result;
    } catch (error) {
        console.error('Error getting review cards:', error);

        return {
            success: false,
            message: error.message || 'An unexpected error occurred when getting review cards',
            error
        };
    }
}

export async function addReviewResults(groupId, cardResults) {
    try {
        const token = getAuthToken();

        if (!token) {
            return {
                success: false,
                message: 'Authentication required',
                error: new Error('No auth token')
            };
        }

        const formattedResults = cardResults.map(result => {
            let duration;

            if (typeof result.duration === 'string') {
                const seconds = parseInt(result.duration.replace('s', ''));
                duration = `${seconds}s`;
            } else if (typeof result.duration === 'number') {
                duration = `${result.duration}s`;
            } else {
                duration = result.duration;
            }

            return {
                card_id: parseInt(result.card_id),
                card_answer: result.card_answer,
                duration: duration
            };
        });

        const response = await fetch(`${API_REVIEWS_URL}/review/${groupId}/result`, {
            method: 'POST',
            headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json'
            },
            body: JSON.stringify({
                group_id: parseInt(groupId),
                card_results: formattedResults,
                check_duration: true
            })
        });

        if (!response.ok) {
            let errorMessage;
            try {
                const errorData = await response.json();
                errorMessage = errorData.message || response.statusText;
            } catch (e) {
                errorMessage = response.statusText;
            }

            return {
                success: false,
                message: `Failed to submit review results: ${errorMessage}`,
                error: new Error(errorMessage),
                status: response.status
            };
        }

        return {
            success: true
        };
    } catch (error) {
        console.error('Error submitting review results:', error);

        return {
            success: false,
            message: error.message || 'An unexpected error occurred when submitting review results',
            error
        };
    }
}